import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '../../auth/types';
import { useUIScale, UI_SCALE_CONFIG } from '../../ui-scale';
import { LANGUAGES } from '../../i18n/config';
import './user-avatar.css';

/**
 * Props para UserAvatar
 */
export interface UserAvatarProps {
  user: User;
  onLogout: () => void;
}

/**
 * Avatar circular con dropdown menu
 *
 * @remarks
 * Componente que muestra un avatar circular con las iniciales del usuario.
 * Al hacer click, despliega un menú con:
 * - Nombre completo y email del usuario
 * - Control de escala de UI (Tamaño de UI)
 * - Selector de idioma (ES / EN / PT / FR)
 * - Botón de logout
 *
 * @example
 * ```tsx
 * <UserAvatar user={currentUser} onLogout={handleLogout} />
 * ```
 */
export function UserAvatar({ user, onLogout }: UserAvatarProps): JSX.Element {
  const { t, i18n } = useTranslation('admin');
  const { scale, setScale, increaseScale, decreaseScale } = useUIScale();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Construir fullName desde firstName y lastName
  const fullName = `${user.firstName} ${user.lastName}`.trim();

  const percentage = Math.round(scale * 100);
  const isMin = scale <= UI_SCALE_CONFIG.MIN;
  const isMax = scale >= UI_SCALE_CONFIG.MAX;

  /**
   * Extrae las iniciales del nombre completo
   */
  const getInitials = (fullName: string): string => {
    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = (): void => {
    setIsMenuOpen(false);
    onLogout();
  };

  const handleLanguageChange = (langCode: string): void => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('wayline_language', langCode);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setScale(parseFloat(e.target.value));
  };

  /**
   * Hook para cerrar el menú al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="user-avatar-container">
      {/* Avatar circular */}
      <button
        ref={buttonRef}
        className="user-avatar"
        onClick={toggleMenu}
        aria-label="Menú de usuario"
        aria-expanded={isMenuOpen}
        aria-haspopup="true"
      >
        <div className="avatar-circle">{getInitials(fullName)}</div>
      </button>

      {/* Dropdown menu (condicional) */}
      {isMenuOpen && (
        <div className="user-menu" ref={menuRef} role="menu">

          {/* ── Sección: Identidad ── */}
          <div className="user-menu-header">
            <div className="user-fullname">{fullName}</div>
            <div className="user-email">{user.email}</div>
          </div>

          <div className="user-menu-divider" />

          {/* ── Sección: Tamaño de UI ── */}
          <div className="user-menu-section">
            <div className="user-menu-section-label">Tamaño de UI</div>
            <div className="user-menu-scale-row">
              <button
                className="user-menu-scale-btn"
                onClick={decreaseScale}
                disabled={isMin}
                title="Disminuir tamaño (-5%)"
                aria-label="Disminuir tamaño de UI"
                type="button"
              >
                −
              </button>
              <input
                type="range"
                min={UI_SCALE_CONFIG.MIN}
                max={UI_SCALE_CONFIG.MAX}
                step={UI_SCALE_CONFIG.STEP}
                value={scale}
                onChange={handleSliderChange}
                className="user-menu-scale-slider"
                aria-label="Ajustar tamaño de interfaz"
              />
              <button
                className="user-menu-scale-btn"
                onClick={increaseScale}
                disabled={isMax}
                title="Aumentar tamaño (+5%)"
                aria-label="Aumentar tamaño de UI"
                type="button"
              >
                +
              </button>
              <span className="user-menu-scale-value">{percentage}%</span>
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* ── Sección: Idioma ── */}
          <div className="user-menu-section">
            <div className="user-menu-section-label">{t('language.label')}</div>
            <div className="user-menu-languages">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  className={`user-menu-lang-btn ${i18n.language === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                  role="menuitem"
                  type="button"
                >
                  <span className="user-menu-lang-code">{lang.code.toUpperCase()}</span>
                  <span className="user-menu-lang-name">{lang.nativeName}</span>
                  {i18n.language === lang.code && (
                    <span className="user-menu-lang-check" aria-label="Idioma activo">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* ── Sección: Logout ── */}
          <button
            className="user-menu-item"
            onClick={handleLogout}
            role="menuitem"
          >
            {t('topbar.logout')}
          </button>

        </div>
      )}
    </div>
  );
}
